// Small moving parts shared by every screen: counts that roll, the AI orb, the check that draws, skeletons.
import { createElement, useEffect, useLayoutEffect, useRef, type CSSProperties, type HTMLAttributes, type RefObject } from 'react';
import { animate, motion, useMotionValue, useReducedMotion, useSpring, useTransform, type AnimationPlaybackControls, type MotionValue } from 'motion/react';
import { duration, ease, prefersReducedMotion, spring } from '../lib/motion';

/** A number that rolls to its new value on a snappy spring. Tabular figures, so nothing around it shifts. */
export function Count({ value, className = '' }: { value: number; className?: string }) {
  const reduce = useReducedMotion();
  const mv = useSpring(value, spring.snappy);
  // The first real value (data arriving) is shown as it is; only later changes roll.
  const settled = useRef(value !== 0);
  useEffect(() => {
    if (reduce || !settled.current) { mv.jump(value); if (value !== 0) settled.current = true; return; }
    mv.set(value);
  }, [value, reduce, mv]);
  const text = useTransform(mv, v => String(Math.round(v)));
  return <motion.span className={`count ${className}`}>{text}</motion.span>;
}

export type OrbState = 'idle' | 'thinking' | 'listening';

/**
 * The AI orb. Idle: it breathes. Thinking: it pulses faster and its light turns. Listening: it swells with the
 * voice (level 0 to 1, from the microphone). All transform and opacity, so it stays at 60fps.
 * breathe: whether an idle orb breathes. By default only a large one does (the Assistant's, the voice orb): the small
 * orbs that label rows, sections and the sidebar stay still. Breathing labels (one per notification) kept the
 * compositor busy every frame, and every click waited for that frame.
 */
export function Orb({ size = 14, state = 'idle', level, className = '', breathe }: { size?: number; state?: OrbState; level?: MotionValue<number>; className?: string; breathe?: boolean }) {
  const reduce = useReducedMotion();
  const zero = useMotionValue(0);
  const lv = useSpring(level ?? zero, { stiffness: 600, damping: 30 });
  const scale = useTransform(lv, v => 1 + Math.min(1, v) * 0.32);
  const halo = useTransform(lv, v => 0.35 + Math.min(1, v) * 0.65);
  const style = { width: size, height: size, '--orb': `${size}px` } as CSSProperties;
  return (
    <span className={`orb3 ${state}${state === 'idle' && !(breathe ?? size >= 20) ? ' still' : ''} ${className}`} style={style} aria-hidden="true">
      <motion.span className="halo" style={state === 'listening' ? { opacity: halo, scale } : undefined}
        animate={state === 'thinking' && !reduce ? { opacity: [0.35, 0.9, 0.35], scale: [1, 1.35, 1] } : undefined}
        transition={state === 'thinking' ? { duration: 1.1, repeat: Infinity, ease: 'easeInOut' } : undefined} />
      <motion.span className="core" style={state === 'listening' ? { scale } : undefined}
        animate={state === 'thinking' && !reduce ? { scale: [0.92, 1.08, 0.92] } : undefined}
        transition={state === 'thinking' ? { duration: 1.1, repeat: Infinity, ease: 'easeInOut' } : undefined} />
      <motion.span className="shine" animate={state === 'thinking' && !reduce ? { rotate: 360 } : { rotate: 0 }}
        transition={state === 'thinking' ? { duration: 1.6, repeat: Infinity, ease: 'linear' } : { duration: duration.base, ease }} />
    </span>
  );
}

/** A check mark that draws itself (success moments). */
export function DrawnCheck({ size = 16 }: { size?: number }) {
  return (
    <motion.svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1, transition: spring.bouncy }}>
      <motion.path d="M5 12.5l4.5 4.5L19 7.5" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: duration.slow, ease }} />
    </motion.svg>
  );
}

/** A grey block shaped like the content that is coming. Shimmer is opacity only. */
export function Skel({ w = '100%', h = 12, r = 6, style }: { w?: number | string; h?: number; r?: number; style?: CSSProperties }) {
  return <span className="skel" style={{ width: w, height: h, borderRadius: r, ...style }} aria-hidden="true" />;
}

/** Skeleton rows for a list of items (Requests, Notifications, the assistant's rail). */
export function SkelList({ rows = 4, variant = 'item' }: { rows?: number; variant?: 'item' | 'note' | 'conv' }) {
  return (
    <div className={`skl ${variant}`} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div className="sk" key={i}>
          {variant !== 'conv' && <Skel w={variant === 'note' ? 30 : 32} h={variant === 'note' ? 30 : 32} r={variant === 'note' ? 15 : 9} />}
          <div className="skb">
            <Skel w={variant === 'conv' ? '70%' : '40%'} h={10} />
            <Skel w={i % 2 ? '85%' : '65%'} h={13} />
            {variant === 'item' && <Skel w="55%" h={10} />}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * No jumps: when something in a column changes size (data arriving, a brief rewriting itself, a panel appearing or
 * going), the blocks below it glide to their new place on the gentle spring instead of snapping. Each direct child
 * that moved is put back where it was and springs home (a transform, so nothing is laid out twice). It happens
 * before the frame is painted (a MutationObserver runs right after the DOM changes, a ResizeObserver before paint),
 * so the snap is never seen.
 * Off with reduced motion: then things simply move.
 */
export function useGlide(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const kids = () => [...el.children].filter((c): c is HTMLElement => c instanceof HTMLElement && !['absolute', 'fixed'].includes(getComputedStyle(c).position));
    let tops = new Map(kids().map(c => [c, c.offsetTop]));
    const moving = new Map<HTMLElement, AnimationPlaybackControls>();
    const check = () => {
      const now = kids();
      const next = new Map(now.map(c => [c, c.offsetTop]));
      if (!prefersReducedMotion()) {
        for (const c of now) {
          const before = tops.get(c), after = next.get(c)!;
          if (before === undefined || Math.abs(before - after) < 1) continue;
          // Start from where it is on screen now (it may be gliding already).
          const t = getComputedStyle(c).transform;
          const shown = t && t !== 'none' ? new DOMMatrixReadOnly(t).m42 : 0;
          moving.get(c)?.stop();
          // Put it back now, in this frame (Motion's first frame comes a frame later, which would show the snap).
          const from = before - after + shown;
          c.style.transform = `translateY(${from}px)`;
          moving.set(c, animate(c, { y: [from, 0] }, spring.gentle));
        }
      }
      tops = next;
      for (const c of now) ro.observe(c);
    };
    const ro = new ResizeObserver(check);
    const mo = new MutationObserver(check);
    for (const c of kids()) ro.observe(c);
    // Any change inside (React's commit) is seen right away, in a microtask before anything else runs or paints;
    // the ResizeObserver catches size changes that are not DOM changes (an image or a font arriving).
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => { ro.disconnect(); mo.disconnect(); for (const a of moving.values()) a.stop(); };
  }, [ref]);
}

/** A column whose blocks glide when one above them changes size (see useGlide). */
export function Glide({ as = 'div', ...rest }: HTMLAttributes<HTMLElement> & { as?: 'div' | 'main' | 'aside' | 'section' }) {
  const ref = useRef<HTMLElement>(null);
  useGlide(ref);
  return createElement(as, { ref, ...rest });
}
